import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Plus, Trash2, FileText, Users, ListOrdered, Store, StickyNote, Copy, ArrowUp, ArrowDown, ScrollText, Wallet } from 'lucide-react';
import { useI18n } from '../../context/I18nContext';
import { useTranslations } from '../../hooks/useTranslations';
import { Invoice, InvoiceLineItem, Payment, Project } from '../../types';
import { InvoiceBuyerProfile, InvoiceSellerProfile } from '../../types/invoiceSeller';
import Input from '../ui/Input';
import DatePicker from '../ui/DatePicker';
import TextArea from '../ui/TextArea';
import Button from '../ui/Button';
import Select from '../ui/Select';
import CollapsibleSection from '../ui/CollapsibleSection';
import ActionIconButton from '../ui/ActionIconButton';
import { invoicesService, paymentsService, profilesService } from '../../services/supabase';
import {
  formatSiretDisplay,
  normalizeSiretInput,
  sirenFromSiret,
} from '../../utils/siret';
import {
  formatIbanDisplay,
  normalizeBic,
  normalizeCountryCode,
  normalizeIban,
  normalizePostalCode,
  normalizeVatNumber,
} from '../../utils/billingIdentity';
import { invoiceLinesTotal } from '../../utils/invoiceDownload';
import InvoicePreview from './InvoicePreview';
import { PAGE_GAP } from '../../styles/pageLayout';

export type { InvoiceSellerProfile };

export type InvoicePaymentMethod = 'transfer' | 'paypal' | 'check' | 'other';
export type InvoicePaymentDueDelay = 'receipt' | '15' | '30' | '45' | '60';

interface InvoiceFormData {
  invoiceName: string;
  invoiceNumber: string;
  issuedAt: string;
  issuedAtObj?: Date | null;
  notes?: string;
  paymentDueDelay: InvoicePaymentDueDelay;
  paymentTermsNotes?: string;
  paymentMethod: InvoicePaymentMethod;
  iban: string;
  bic: string;
  accountHolder: string;
  name: string;
  company: string;
  siret: string;
  vatNumber: string;
  addressLine1: string;
  addressLine2: string;
  postalCode: string;
  city: string;
  countryCode: string;
  buyerName: string;
  buyerCompany: string;
  buyerSiret: string;
  buyerVatNumber: string;
  buyerAddressLine1: string;
  buyerAddressLine2: string;
  buyerPostalCode: string;
  buyerCity: string;
  buyerCountryCode: string;
}

const formatLocalDateInput = (date: Date): string => {
  const pad2 = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
};

const parseLocalDateInput = (value: string): Date | null => {
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  const [, y, m, d] = match;
  const dt = new Date(Number(y), Number(m) - 1, Number(d), 0, 0, 0, 0);
  return Number.isNaN(dt.getTime()) ? null : dt;
};

const emptyToNull = (value: string | undefined | null): string | null => {
  const trimmed = (value || '').trim();
  return trimmed ? trimmed : null;
};

const sellerFromForm = (data: InvoiceFormData): InvoiceSellerProfile => {
  const siretDigits = normalizeSiretInput(data.siret || '');
  const vat = normalizeVatNumber(data.vatNumber || '');
  const country = normalizeCountryCode(data.countryCode || '') || 'FR';
  return {
    name: data.name.trim() || undefined,
    company: emptyToNull(data.company) || undefined,
    siret: siretDigits.length === 14 ? siretDigits : null,
    vatNumber: vat || null,
    addressLine1: emptyToNull(data.addressLine1),
    addressLine2: emptyToNull(data.addressLine2),
    postalCode: emptyToNull(normalizePostalCode(data.postalCode || '')),
    city: emptyToNull(data.city),
    countryCode: country,
  };
};

const normalizeSellerForCompare = (seller: InvoiceSellerProfile) => ({
  name: (seller.name || '').trim(),
  company: (seller.company || '').trim(),
  siret: normalizeSiretInput(seller.siret || ''),
  vatNumber: normalizeVatNumber(seller.vatNumber || ''),
  addressLine1: (seller.addressLine1 || '').trim(),
  addressLine2: (seller.addressLine2 || '').trim(),
  postalCode: normalizePostalCode(seller.postalCode || ''),
  city: (seller.city || '').trim(),
  countryCode: normalizeCountryCode(seller.countryCode || '') || 'FR',
});

const sellersEqual = (a: InvoiceSellerProfile, b: InvoiceSellerProfile) => {
  const left = normalizeSellerForCompare(a);
  const right = normalizeSellerForCompare(b);
  return (Object.keys(left) as Array<keyof typeof left>).every((key) => left[key] === right[key]);
};

const buyerFromForm = (data: InvoiceFormData): InvoiceBuyerProfile => {
  const siretDigits = normalizeSiretInput(data.buyerSiret || '');
  const vat = normalizeVatNumber(data.buyerVatNumber || '');
  const country = normalizeCountryCode(data.buyerCountryCode || '') || 'FR';
  return {
    name: data.buyerName.trim() || undefined,
    company: emptyToNull(data.buyerCompany) || undefined,
    siret: siretDigits.length === 14 ? siretDigits : null,
    vatNumber: vat || null,
    addressLine1: emptyToNull(data.buyerAddressLine1),
    addressLine2: emptyToNull(data.buyerAddressLine2),
    postalCode: emptyToNull(normalizePostalCode(data.buyerPostalCode || '')),
    city: emptyToNull(data.buyerCity),
    countryCode: country,
  };
};

export interface IssueInvoiceCreatedPayload {
  invoice: Invoice;
  seller: InvoiceSellerProfile;
  buyer: InvoiceBuyerProfile;
  lineItems: InvoiceLineItem[];
  currency: string;
  dueDate: Date;
  invoiceName?: string;
  paymentTerms?: string;
  paymentMethod?: InvoicePaymentMethod;
  iban?: string;
  bic?: string;
  accountHolder?: string;
}

interface IssueInvoiceFormProps {
  payment: Payment;
  project?: Project | null;
  onCancel: () => void;
  onCreated: (payload: IssueInvoiceCreatedPayload) => void;
  /** Rendered above the form sections in the left column (keeps preview aligned). */
  leading?: React.ReactNode;
}

type SectionId = 'info' | 'buyer' | 'items' | 'seller' | 'notes' | 'paymentTerms' | 'payment';

type LineDraft = {
  id: string;
  description: string;
  details: string;
  quantity: string;
  unitPrice: string;
  /** 'na' | '0' | '5.5' | '10' | '20' */
  vatRate: string;
};

const newLineId = (): string =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `line-${Date.now()}-${Math.random().toString(16).slice(2)}`;

const emptyLineDraft = (): LineDraft => ({
  id: newLineId(),
  description: '',
  details: '',
  quantity: '1',
  unitPrice: '',
  vatRate: 'na',
});

const partySummary = (party: InvoiceSellerProfile): string | undefined => {
  const main = party.company?.trim() || party.name?.trim();
  if (!main) return undefined;
  const place = [party.postalCode, party.city].filter(Boolean).join(' ').trim();
  return place ? `${main} · ${place}` : main;
};

const CURRENCY_OPTIONS = [
  { value: 'EUR', label: '€ Euro' },
  { value: 'USD', label: '$ Dollar' },
  { value: 'GBP', label: '£ Pound' },
];

const VAT_RATE_VALUES = ['na', '0', '2.1', '5.5', '10', '20'] as const;

const PAYMENT_DUE_DELAY_VALUES: InvoicePaymentDueDelay[] = [
  'receipt',
  '15',
  '30',
  '45',
  '60',
];

const dueDateFromDelay = (
  issuedAt: Date,
  delay: InvoicePaymentDueDelay
): Date => {
  if (delay === 'receipt') return new Date(issuedAt);
  const days = Number(delay);
  const due = new Date(issuedAt);
  due.setDate(due.getDate() + (Number.isFinite(days) ? days : 30));
  return due;
};

const buildPaymentTermsText = (
  delay: InvoicePaymentDueDelay,
  notes: string | undefined,
  t: (key: string, params?: Record<string, string | number>) => string
): string => {
  const phrase =
    delay === 'receipt'
      ? t('invoices.paymentDuePhraseReceipt')
      : t('invoices.paymentDuePhraseDays', { days: delay });
  const extra = (notes || '').trim();
  return extra ? `${phrase}\n${extra}` : phrase;
};

const IssueInvoiceForm: React.FC<IssueInvoiceFormProps> = ({
  payment,
  project,
  onCancel,
  onCreated,
  leading,
}) => {
  const { t } = useI18n();
  const { formatCurrency, formatDateShort } = useTranslations();
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [linesError, setLinesError] = useState<string | null>(null);
  const [ready, setReady] = useState(false);
  const baselineSellerRef = useRef<InvoiceSellerProfile | null>(null);
  const [currency, setCurrency] = useState(payment.currency || 'EUR');
  const [lines, setLines] = useState<LineDraft[]>([]);
  const [openSections, setOpenSections] = useState<Record<SectionId, boolean>>({
    info: true,
    buyer: true,
    items: true,
    seller: true,
    paymentTerms: false,
    payment: false,
    notes: false,
  });

  const toggleSection = (id: SectionId) => {
    setOpenSections((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const openSection = (id: SectionId) => {
    setOpenSections((prev) => (prev[id] ? prev : { ...prev, [id]: true }));
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<InvoiceFormData>({
    defaultValues: {
      invoiceName: '',
      invoiceNumber: '',
      issuedAt: '',
      issuedAtObj: null,
      notes: '',
      paymentDueDelay: '30',
      paymentTermsNotes: '',
      paymentMethod: 'transfer',
      iban: '',
      bic: '',
      accountHolder: '',
      name: '',
      company: '',
      siret: '',
      vatNumber: '',
      addressLine1: '',
      addressLine2: '',
      postalCode: '',
      city: '',
      countryCode: 'FR',
      buyerName: '',
      buyerCompany: '',
      buyerSiret: '',
      buyerVatNumber: '',
      buyerAddressLine1: '',
      buyerAddressLine2: '',
      buyerPostalCode: '',
      buyerCity: '',
      buyerCountryCode: 'FR',
    },
  });

  const formValues = watch();
  const issuedAtObj = formValues.issuedAtObj;
  const derivedSiren = useMemo(
    () => sirenFromSiret(formValues.siret || ''),
    [formValues.siret]
  );
  const derivedBuyerSiren = useMemo(
    () => sirenFromSiret(formValues.buyerSiret || ''),
    [formValues.buyerSiret]
  );

  const liveSeller = useMemo(() => sellerFromForm(formValues), [formValues]);
  const liveBuyer = useMemo(() => buyerFromForm(formValues), [formValues]);

  const statusLabel = useMemo(() => {
    if (payment.status === 'paid') return t('payments.statusLabels.paid');
    if (payment.status === 'overdue') return t('payments.statusLabels.overdue');
    return t('payments.statusLabels.sent');
  }, [payment, t]);

  const previewIssuedAtLabel = useMemo(() => {
    const date =
      formValues.issuedAtObj ||
      parseLocalDateInput(formValues.issuedAt || '') ||
      null;
    return date ? formatDateShort(date) : '—';
  }, [formValues.issuedAt, formValues.issuedAtObj, formatDateShort]);

  useEffect(() => {
    let cancelled = false;
    setFormError(null);
    setSubmitting(false);
    setReady(false);
    baselineSellerRef.current = null;

    (async () => {
      const [suggested, profile] = await Promise.all([
        invoicesService.suggestNextNumber(),
        profilesService.getProfile({ force: true }),
      ]);
      if (cancelled) return;

      const seller: InvoiceSellerProfile = {
        name: typeof profile?.name === 'string' ? profile.name : '',
        company: typeof profile?.company === 'string' ? profile.company : '',
        siret: typeof profile?.siret === 'string' ? profile.siret : null,
        vatNumber: typeof profile?.vat_number === 'string' ? profile.vat_number : null,
        addressLine1: typeof profile?.address_line1 === 'string' ? profile.address_line1 : null,
        addressLine2: typeof profile?.address_line2 === 'string' ? profile.address_line2 : null,
        postalCode: typeof profile?.postal_code === 'string' ? profile.postal_code : null,
        city: typeof profile?.city === 'string' ? profile.city : null,
        countryCode: typeof profile?.country_code === 'string' ? profile.country_code : 'FR',
      };
      baselineSellerRef.current = seller;

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      reset({
        invoiceName: '',
        invoiceNumber: suggested,
        issuedAt: formatLocalDateInput(today),
        issuedAtObj: today,
        notes: '',
        paymentDueDelay: '30',
        paymentTermsNotes: '',
        paymentMethod: 'transfer',
        iban: '',
        bic: '',
        accountHolder: '',
        name: seller.name || '',
        company: seller.company || '',
        siret: formatSiretDisplay(seller.siret || ''),
        vatNumber: seller.vatNumber || '',
        addressLine1: seller.addressLine1 || '',
        addressLine2: seller.addressLine2 || '',
        postalCode: seller.postalCode || '',
        city: seller.city || '',
        countryCode: seller.countryCode || 'FR',
        buyerName: '',
        buyerCompany: '',
        buyerSiret: '',
        buyerVatNumber: '',
        buyerAddressLine1: '',
        buyerAddressLine2: '',
        buyerPostalCode: '',
        buyerCity: '',
        buyerCountryCode: 'FR',
      });
      setCurrency(payment.currency || 'EUR');
      setLines([emptyLineDraft()]);
      setLinesError(null);
      // Seller rarely changes once filled — keep collapsed when profile already has a name.
      setOpenSections({
        info: true,
        buyer: true,
        items: true,
        seller: !Boolean(seller.name?.trim()),
        paymentTerms: false,
        payment: false,
        notes: false,
      });
      setReady(true);
    })();

    return () => {
      cancelled = true;
    };
  }, [payment.id, payment.currency, reset]);

  useEffect(() => {
    const buyerKeys = [
      'buyerName',
      'buyerCompany',
      'buyerSiret',
      'buyerVatNumber',
      'buyerAddressLine1',
      'buyerAddressLine2',
      'buyerPostalCode',
      'buyerCity',
      'buyerCountryCode',
    ] as const;
    const sellerKeys = [
      'name',
      'company',
      'siret',
      'vatNumber',
      'addressLine1',
      'addressLine2',
      'postalCode',
      'city',
      'countryCode',
    ] as const;
    if (errors.invoiceNumber || errors.issuedAt) openSection('info');
    if (buyerKeys.some((key) => errors[key])) openSection('buyer');
    if (sellerKeys.some((key) => errors[key])) openSection('seller');
    if (linesError) openSection('items');
  }, [errors, linesError]);

  const vatRateOptions = useMemo(
    () =>
      VAT_RATE_VALUES.map((value) => ({
        value,
        label:
          value === 'na'
            ? t('invoices.vatNotApplicable')
            : `${value.replace('.', ',')} %`,
      })),
    [t]
  );

  const formatVatLabel = (vatRate: number | null | undefined): string => {
    if (vatRate === null || vatRate === undefined) return '';
    return `${String(vatRate).replace('.', ',')} %`;
  };

  const parsedLines: InvoiceLineItem[] = useMemo(
    () =>
      lines.map((line) => ({
        id: line.id,
        description: line.description.trim(),
        details: line.details.trim() || undefined,
        quantity: Math.max(0, Number(line.quantity) || 0),
        unitPrice: Number(String(line.unitPrice).replace(',', '.')) || 0,
        vatRate: line.vatRate === 'na' || line.vatRate === '' ? null : Number(line.vatRate),
      })),
    [lines]
  );

  const linesTotal = useMemo(() => invoiceLinesTotal(parsedLines), [parsedLines]);

  const updateLine = (id: string, patch: Partial<LineDraft>) => {
    setLines((prev) => prev.map((line) => (line.id === id ? { ...line, ...patch } : line)));
  };

  const addLine = () => {
    setLines((prev) => [...prev, emptyLineDraft()]);
    openSection('items');
  };

  const duplicateLine = (id: string) => {
    setLines((prev) => {
      const index = prev.findIndex((line) => line.id === id);
      if (index < 0) return prev;
      const source = prev[index];
      const copy: LineDraft = {
        id: newLineId(),
        description: source.description,
        details: source.details,
        quantity: source.quantity,
        unitPrice: source.unitPrice,
        vatRate: source.vatRate,
      };
      const next = [...prev];
      next.splice(index + 1, 0, copy);
      return next;
    });
    openSection('items');
  };

  const moveLine = (id: string, direction: -1 | 1) => {
    setLines((prev) => {
      const index = prev.findIndex((line) => line.id === id);
      if (index < 0) return prev;
      const target = index + direction;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      const [item] = next.splice(index, 1);
      next.splice(target, 0, item);
      return next;
    });
  };

  const removeLine = (id: string) => {
    setLines((prev) => prev.filter((line) => line.id !== id));
  };

  const onSubmit = async (data: InvoiceFormData) => {
    const issuedAt =
      data.issuedAtObj ||
      parseLocalDateInput(data.issuedAt) ||
      new Date();
    const dueDate = dueDateFromDelay(
      issuedAt,
      data.paymentDueDelay || '30'
    );
    setSubmitting(true);
    setFormError(null);
    setLinesError(null);

    const validLines = parsedLines.filter(
      (line) => line.description && line.quantity > 0
    );
    if (validLines.length === 0) {
      setLinesError(t('invoices.linesRequired'));
      openSection('items');
      setSubmitting(false);
      return;
    }

    const seller = sellerFromForm(data);
    const buyer = buyerFromForm(data);
    const baseline = baselineSellerRef.current;

    // Thin DB stub (number / date / notes / payment link). Client + lines live in the PDF only.
    const result = await invoicesService.create({
      paymentId: payment.id,
      invoiceNumber: data.invoiceNumber.trim(),
      issuedAt,
      notes: data.notes?.trim() || undefined,
      status: 'issued',
    });

    if (!result || 'error' in result) {
      setSubmitting(false);
      if (result && result.error === 'NUMBER_TAKEN') {
        setFormError(t('invoices.numberTaken'));
      } else {
        setFormError(t('invoices.createError'));
      }
      return;
    }

    const paymentDueKey = formatLocalDateInput(
      payment.dueDate instanceof Date ? payment.dueDate : new Date(payment.dueDate)
    );
    if (formatLocalDateInput(dueDate) !== paymentDueKey) {
      await paymentsService.update(payment.id, { dueDate });
    }

    if (baseline && !sellersEqual(seller, baseline)) {
      const billingPayload = {
        name: seller.name || '',
        company: seller.company || undefined,
        siret: seller.siret,
        vat_number: seller.vatNumber,
        address_line1: seller.addressLine1,
        address_line2: seller.addressLine2,
        postal_code: seller.postalCode,
        city: seller.city,
        country_code: seller.countryCode,
      };
      try {
        const updated = await profilesService.updateProfile(billingPayload);
        if (!updated) {
          const existing = await profilesService.getProfile({ force: true });
          if (!existing) {
            await profilesService.createProfile(billingPayload);
          }
        }
      } catch (err) {
        console.error('Error syncing seller profile from invoice form:', err);
      }
    }

    setSubmitting(false);
    onCreated({
      invoice: result,
      seller,
      buyer,
      lineItems: validLines,
      currency,
      dueDate,
      invoiceName: data.invoiceName?.trim() || undefined,
      paymentTerms: buildPaymentTermsText(
        data.paymentDueDelay || '30',
        data.paymentTermsNotes,
        t
      ),
      paymentMethod: data.paymentMethod || 'transfer',
      iban: normalizeIban(data.iban || '') || undefined,
      bic: normalizeBic(data.bic || '') || undefined,
      accountHolder: data.accountHolder?.trim() || undefined,
    });
  };

  const amountLabel = formatCurrency(linesTotal, currency);
  const projectLabel = project?.name || '—';
  const sellerSummary = partySummary(liveSeller);
  const buyerSummary = partySummary(liveBuyer);
  const infoSummary = [formValues.invoiceName, formValues.invoiceNumber, previewIssuedAtLabel]
    .filter((v) => v && v !== '—')
    .join(' · ');
  const firstLine = parsedLines.find((line) => line.description);
  const itemsSummary = firstLine
    ? `${firstLine.description} · ${amountLabel}`
    : lines.length > 0
      ? amountLabel
      : undefined;
  const notesSummary = formValues.notes?.trim()
    ? formValues.notes.trim().slice(0, 48) + (formValues.notes.trim().length > 48 ? '…' : '')
    : undefined;
  const paymentDueDelayOptions = useMemo(
    () =>
      PAYMENT_DUE_DELAY_VALUES.map((value) => ({
        value,
        label:
          value === 'receipt'
            ? t('invoices.paymentDueReceipt')
            : t('invoices.paymentDueDays', { days: value }),
      })),
    [t]
  );

  const composedPaymentTerms = useMemo(
    () =>
      buildPaymentTermsText(
        formValues.paymentDueDelay || '30',
        formValues.paymentTermsNotes,
        t
      ),
    [formValues.paymentDueDelay, formValues.paymentTermsNotes, t]
  );

  const paymentTermsSummary = composedPaymentTerms
    ? composedPaymentTerms.split('\n')[0].slice(0, 48) +
      (composedPaymentTerms.split('\n')[0].length > 48 ? '…' : '')
    : undefined;

  const paymentMethodLabel = useMemo(() => {
    const method = formValues.paymentMethod || 'transfer';
    if (method === 'paypal') return t('invoices.paymentMethodPaypal');
    if (method === 'check') return t('invoices.paymentMethodCheck');
    if (method === 'other') return t('invoices.paymentMethodOther');
    return t('invoices.paymentMethodTransfer');
  }, [formValues.paymentMethod, t]);
  const paymentSummary = [paymentMethodLabel, formValues.accountHolder?.trim()]
    .filter(Boolean)
    .join(' · ');

  const paymentMethodOptions = useMemo(
    () => [
      { value: 'transfer', label: t('invoices.paymentMethodTransfer') },
      { value: 'paypal', label: t('invoices.paymentMethodPaypal') },
      { value: 'check', label: t('invoices.paymentMethodCheck') },
      { value: 'other', label: t('invoices.paymentMethodOther') },
    ],
    [t]
  );

  const previewLines = parsedLines.map((line) => ({
    ...line,
    unitPriceLabel: formatCurrency(line.unitPrice, currency),
    vatLabel: formatVatLabel(line.vatRate),
    totalLabel: formatCurrency(line.quantity * line.unitPrice, currency),
  }));

  const sectionComplete = useMemo(() => {
    const info =
      Boolean(formValues.invoiceName?.trim()) &&
      Boolean(formValues.invoiceNumber?.trim()) &&
      Boolean(
        formValues.issuedAtObj || parseLocalDateInput(formValues.issuedAt || '')
      );
    const seller = Boolean(liveSeller.name?.trim());
    const buyer = Boolean(liveBuyer.name?.trim());
    const items = parsedLines.some(
      (line) => line.description && line.quantity > 0
    );
    // Optional: check only when something beyond empty/defaults is filled.
    const notes = Boolean(formValues.notes?.trim());
    const paymentTerms =
      Boolean(formValues.paymentTermsNotes?.trim()) ||
      (formValues.paymentDueDelay || '30') !== '30';
    const paymentSection =
      (formValues.paymentMethod || 'transfer') !== 'transfer' ||
      Boolean(formValues.accountHolder?.trim()) ||
      Boolean(formValues.iban?.trim()) ||
      Boolean(formValues.bic?.trim());
    return {
      info,
      seller,
      buyer,
      items,
      notes,
      paymentTerms,
      payment: paymentSection,
    } as Record<SectionId, boolean>;
  }, [
    formValues,
    liveSeller.name,
    liveBuyer.name,
    parsedLines,
  ]);

  const requiredSectionIds: SectionId[] = [
    'info',
    'seller',
    'buyer',
    'items',
    'paymentTerms',
    'payment',
  ];
  // Done = content OK (open or closed). Collapsed state only affects the summary line.
  const isSectionDone = (id: SectionId) => sectionComplete[id];
  const completedCount = requiredSectionIds.filter((id) => isSectionDone(id)).length;
  const totalSections = requiredSectionIds.length;
  const progressPct = Math.round((completedCount / totalSections) * 100);

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4 pb-20">
      <div className={`grid grid-cols-1 xl:grid-cols-2 ${PAGE_GAP}`}>
        <div className={`min-w-0 flex flex-col ${PAGE_GAP}`}>
          {leading ? <div className="min-w-0">{leading}</div> : null}
          <div className="flex items-center gap-3 px-0.5">
            <div
              className="h-1.5 min-w-0 flex-1 overflow-hidden rounded-full bg-gray-200/80"
              role="progressbar"
              aria-valuenow={completedCount}
              aria-valuemin={0}
              aria-valuemax={totalSections}
            >
              <div
                className="h-full rounded-full bg-primary-500 transition-all duration-300 ease-out"
                style={{ width: `${progressPct}%` }}
              />
            </div>
            <p className="shrink-0 text-sm text-gray-600">
              {t('invoices.sectionsCompleted', {
                done: completedCount,
                total: totalSections,
              })}
            </p>
          </div>

          <CollapsibleSection
            title={t('invoices.invoiceInfo')}
            open={openSections.info}
            onToggle={() => toggleSection('info')}
            summary={infoSummary || undefined}
            complete={isSectionDone('info')}
            icon={<FileText className="h-4 w-4" aria-hidden />}
          >
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 sm:gap-4">
                <div className="min-w-0">
                  <Input
                    label={t('invoices.invoiceName')}
                    {...register('invoiceName')}
                    placeholder={t('invoices.invoiceNamePlaceholder')}
                    autoComplete="off"
                  />
                </div>
                <div className="min-w-0">
                  <Input
                    label={t('invoices.invoiceNumber')}
                    {...register('invoiceNumber', {
                      required: t('invoices.invoiceNumberRequired') as string,
                    })}
                    error={errors.invoiceNumber?.message}
                    autoComplete="off"
                  />
                </div>
              </div>
              <div className="min-w-0 sm:max-w-xs">
                <input type="hidden" {...register('issuedAt', { required: true })} />
                <DatePicker
                  label={t('invoices.issuedAt')}
                  value={issuedAtObj || null}
                  onChange={(date) => {
                    setValue('issuedAtObj', date);
                    setValue('issuedAt', date ? formatLocalDateInput(date) : '', {
                      shouldValidate: true,
                      shouldDirty: true,
                    });
                  }}
                  error={errors.issuedAt ? t('invoices.issuedAtRequired') : undefined}
                />
              </div>
            </div>
          </CollapsibleSection>

          <CollapsibleSection
            title={t('invoices.seller')}
            open={openSections.seller}
            onToggle={() => toggleSection('seller')}
            summary={sellerSummary}
            complete={isSectionDone('seller')}
            icon={<Store className="h-4 w-4" aria-hidden />}
          >
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Input
                label={t('settings.name')}
                {...register('name', { required: t('settings.nameRequired') })}
                error={errors.name?.message}
              />
              <Input
                label={t('settings.company')}
                {...register('company')}
                error={errors.company?.message}
                placeholder={t('settings.companyPlaceholder')}
              />
              <div>
                <Input
                  label={t('settings.siretLabel')}
                  inputMode="numeric"
                  autoComplete="off"
                  placeholder={t('settings.siretPlaceholder')}
                  {...register('siret', {
                    onChange: (e) => {
                      const formatted = formatSiretDisplay(e.target.value);
                      setValue('siret', formatted, { shouldDirty: true });
                    },
                  })}
                  error={errors.siret?.message}
                />
                {derivedSiren ? (
                  <p className="mt-1 text-xs text-gray-600">
                    {t('settings.sirenDerived')}:{' '}
                    <span className="font-medium tabular-nums tracking-wide">{derivedSiren}</span>
                  </p>
                ) : null}
              </div>

              <Input
                label={t('settings.vatNumberLabel')}
                autoComplete="off"
                placeholder={t('settings.vatNumberPlaceholder')}
                {...register('vatNumber', {
                  onChange: (e) => {
                    setValue('vatNumber', normalizeVatNumber(e.target.value), {
                      shouldDirty: true,
                    });
                  },
                })}
                error={errors.vatNumber?.message}
              />

              <Input
                label={t('settings.addressLine1Label')}
                autoComplete="street-address"
                placeholder={t('settings.addressLine1Placeholder')}
                {...register('addressLine1')}
                error={errors.addressLine1?.message}
              />
              <Input
                label={t('settings.addressLine2Label')}
                autoComplete="off"
                placeholder={t('settings.addressLine2Placeholder')}
                {...register('addressLine2')}
                error={errors.addressLine2?.message}
              />
              <Input
                label={t('settings.postalCodeLabel')}
                autoComplete="postal-code"
                placeholder={t('settings.postalCodePlaceholder')}
                {...register('postalCode')}
                error={errors.postalCode?.message}
              />
              <Input
                label={t('settings.cityLabel')}
                autoComplete="address-level2"
                placeholder={t('settings.cityPlaceholder')}
                {...register('city')}
                error={errors.city?.message}
              />
              <Input
                label={t('settings.countryCodeLabel')}
                autoComplete="country"
                placeholder="FR"
                maxLength={2}
                {...register('countryCode', {
                  validate: (value) => {
                    const code = normalizeCountryCode(value || '');
                    if (!code) return true;
                    return code.length === 2 || t('settings.countryCodeInvalid');
                  },
                  onChange: (e) => {
                    setValue('countryCode', normalizeCountryCode(e.target.value) || '', {
                      shouldValidate: true,
                      shouldDirty: true,
                    });
                  },
                })}
                error={errors.countryCode?.message}
              />
            </div>
          </CollapsibleSection>

          <CollapsibleSection
            title={t('invoices.billedTo')}
            open={openSections.buyer}
            onToggle={() => toggleSection('buyer')}
            summary={buyerSummary}
            complete={isSectionDone('buyer')}
            icon={<Users className="h-4 w-4" aria-hidden />}
          >
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Input
                label={t('settings.name')}
                {...register('buyerName', { required: t('invoices.buyerNameRequired') })}
                error={errors.buyerName?.message}
              />
              <Input
                label={t('settings.company')}
                {...register('buyerCompany')}
                error={errors.buyerCompany?.message}
                placeholder={t('settings.companyPlaceholder')}
              />
              <div>
                <Input
                  label={t('settings.siretLabel')}
                  inputMode="numeric"
                  autoComplete="off"
                  placeholder={t('settings.siretPlaceholder')}
                  {...register('buyerSiret', {
                    onChange: (e) => {
                      const formatted = formatSiretDisplay(e.target.value);
                      setValue('buyerSiret', formatted, {
                        shouldDirty: true,
                      });
                    },
                  })}
                  error={errors.buyerSiret?.message}
                />
                {derivedBuyerSiren ? (
                  <p className="mt-1 text-xs text-gray-600">
                    {t('settings.sirenDerived')}:{' '}
                    <span className="font-medium tabular-nums tracking-wide">
                      {derivedBuyerSiren}
                    </span>
                  </p>
                ) : null}
              </div>
              <Input
                label={t('settings.vatNumberLabel')}
                autoComplete="off"
                placeholder={t('settings.vatNumberPlaceholder')}
                {...register('buyerVatNumber', {
                  onChange: (e) => {
                    setValue('buyerVatNumber', normalizeVatNumber(e.target.value), {
                      shouldDirty: true,
                    });
                  },
                })}
                error={errors.buyerVatNumber?.message}
              />
              <Input
                label={t('settings.addressLine1Label')}
                autoComplete="street-address"
                placeholder={t('settings.addressLine1Placeholder')}
                {...register('buyerAddressLine1')}
                error={errors.buyerAddressLine1?.message}
              />
              <Input
                label={t('settings.addressLine2Label')}
                autoComplete="off"
                placeholder={t('settings.addressLine2Placeholder')}
                {...register('buyerAddressLine2')}
                error={errors.buyerAddressLine2?.message}
              />
              <Input
                label={t('settings.postalCodeLabel')}
                autoComplete="postal-code"
                placeholder={t('settings.postalCodePlaceholder')}
                {...register('buyerPostalCode')}
                error={errors.buyerPostalCode?.message}
              />
              <Input
                label={t('settings.cityLabel')}
                autoComplete="address-level2"
                placeholder={t('settings.cityPlaceholder')}
                {...register('buyerCity')}
                error={errors.buyerCity?.message}
              />
              <Input
                label={t('settings.countryCodeLabel')}
                autoComplete="country"
                placeholder="FR"
                maxLength={2}
                {...register('buyerCountryCode', {
                  validate: (value) => {
                    const code = normalizeCountryCode(value || '');
                    if (!code) return true;
                    return code.length === 2 || t('settings.countryCodeInvalid');
                  },
                  onChange: (e) => {
                    setValue(
                      'buyerCountryCode',
                      normalizeCountryCode(e.target.value) || '',
                      { shouldValidate: true, shouldDirty: true }
                    );
                  },
                })}
                error={errors.buyerCountryCode?.message}
              />
            </div>
          </CollapsibleSection>

          <CollapsibleSection
            title={t('invoices.lineItems')}
            open={openSections.items}
            onToggle={() => toggleSection('items')}
            summary={itemsSummary}
            complete={isSectionDone('items')}
            icon={<ListOrdered className="h-4 w-4" aria-hidden />}
          >
            <div className="space-y-4">
              <Select
                label={t('projects.currency')}
                value={currency}
                onChange={setCurrency}
                options={CURRENCY_OPTIONS}
              />

              <div className="space-y-3">
                {lines.map((line, index) => {
                  const qty = Math.max(0, Number(line.quantity) || 0);
                  const unit = Number(String(line.unitPrice).replace(',', '.')) || 0;
                  const lineTotalLabel = formatCurrency(qty * unit, currency);
                  return (
                    <div
                      key={line.id}
                      className="rounded-xl border border-gray-200 bg-white p-3 sm:p-4"
                    >
                      <div className="flex gap-2 sm:gap-3">
                        <div className="flex shrink-0 flex-col justify-center gap-0.5 pt-6">
                          <button
                            type="button"
                            onClick={() => moveLine(line.id, -1)}
                            disabled={index === 0}
                            className="inline-flex p-2 rounded-lg text-primary-600 transition-colors hover:text-primary-700 hover:bg-primary-50 disabled:pointer-events-none disabled:opacity-40"
                            aria-label={t('invoices.moveLineUp')}
                            title={t('invoices.moveLineUp')}
                          >
                            <ArrowUp className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => moveLine(line.id, 1)}
                            disabled={index === lines.length - 1}
                            className="inline-flex p-2 rounded-lg text-primary-600 transition-colors hover:text-primary-700 hover:bg-primary-50 disabled:pointer-events-none disabled:opacity-40"
                            aria-label={t('invoices.moveLineDown')}
                            title={t('invoices.moveLineDown')}
                          >
                            <ArrowDown className="h-4 w-4" />
                          </button>
                        </div>

                        <div className="min-w-0 flex-1 space-y-3">
                          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-[minmax(0,1.3fr)_4.5rem_6.5rem_9rem_6.5rem]">
                            <Input
                              label={t('invoices.item')}
                              value={line.description}
                              onChange={(e) =>
                                updateLine(line.id, { description: e.target.value })
                              }
                              placeholder={t('invoices.itemPlaceholder')}
                            />
                            <Input
                              label={t('invoices.qty')}
                              inputMode="decimal"
                              value={line.quantity}
                              onChange={(e) =>
                                updateLine(line.id, { quantity: e.target.value })
                              }
                            />
                            <Input
                              label={t('invoices.unitPrice')}
                              inputMode="decimal"
                              value={line.unitPrice}
                              onChange={(e) =>
                                updateLine(line.id, { unitPrice: e.target.value })
                              }
                            />
                            <Select
                              label={t('invoices.vatRate')}
                              value={line.vatRate || 'na'}
                              onChange={(value) => updateLine(line.id, { vatRate: value })}
                              options={vatRateOptions}
                              showEmptyOptionInList={false}
                              dropdownClassName="!max-h-none !overflow-visible"
                            />
                            <div>
                              <div className="mb-1 text-sm font-medium text-gray-700">
                                {t('invoices.lineTotal')}
                              </div>
                              <div className="flex h-[42px] items-center rounded-xl border border-gray-200 bg-gray-50 px-3.5 text-sm font-semibold tabular-nums text-gray-900">
                                {lineTotalLabel}
                              </div>
                            </div>
                          </div>

                          <div className="flex items-end gap-2">
                            <div className="min-w-0 flex-1">
                              <Input
                                label={t('invoices.itemDetails')}
                                value={line.details}
                                onChange={(e) =>
                                  updateLine(line.id, { details: e.target.value })
                                }
                                placeholder={t('invoices.itemDetailsPlaceholder')}
                              />
                            </div>
                            <div className="flex shrink-0 items-center gap-0.5 pb-0.5">
                              <ActionIconButton
                                icon={Copy}
                                onClick={() => duplicateLine(line.id)}
                                variant="ghost"
                                size="sm"
                                tooltip={t('invoices.duplicateLine')}
                              />
                              <ActionIconButton
                                icon={Trash2}
                                onClick={() => removeLine(line.id)}
                                variant="delete"
                                size="sm"
                                tooltip={t('common.delete')}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {linesError ? (
                <p className="text-sm text-red-600" role="alert">
                  {linesError}
                </p>
              ) : null}

              <div className="flex flex-wrap items-center justify-between gap-3">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={addLine}
                  icon={<Plus className="h-4 w-4" />}
                >
                  {t('invoices.addLine')}
                </Button>
                <div className="text-sm font-semibold tabular-nums text-gray-900">
                  {t('invoices.total')}: {amountLabel}
                </div>
              </div>
            </div>
          </CollapsibleSection>

          <CollapsibleSection
            title={t('invoices.paymentTerms')}
            open={openSections.paymentTerms}
            onToggle={() => toggleSection('paymentTerms')}
            summary={paymentTermsSummary}
            complete={isSectionDone('paymentTerms')}
            icon={<ScrollText className="h-4 w-4" aria-hidden />}
          >
            <div className="space-y-4">
              <Select
                label={t('invoices.paymentDueDelay')}
                value={formValues.paymentDueDelay || '30'}
                onChange={(value) =>
                  setValue('paymentDueDelay', value as InvoicePaymentDueDelay, {
                    shouldDirty: true,
                  })
                }
                options={paymentDueDelayOptions}
                showEmptyOptionInList={false}
                dropdownClassName="!max-h-none !overflow-visible"
              />
              <TextArea
                label={t('invoices.paymentTermsNotes')}
                placeholder={t('invoices.paymentTermsNotesPlaceholder')}
                rows={2}
                {...register('paymentTermsNotes')}
              />
            </div>
          </CollapsibleSection>

          <CollapsibleSection
            title={t('invoices.paymentSection')}
            open={openSections.payment}
            onToggle={() => toggleSection('payment')}
            summary={paymentSummary || undefined}
            complete={isSectionDone('payment')}
            icon={<Wallet className="h-4 w-4" aria-hidden />}
          >
            <div className="space-y-4">
              <Select
                label={t('invoices.paymentMethod')}
                value={formValues.paymentMethod || 'transfer'}
                onChange={(value) =>
                  setValue('paymentMethod', value as InvoicePaymentMethod, {
                    shouldDirty: true,
                  })
                }
                options={paymentMethodOptions}
                showEmptyOptionInList={false}
                dropdownClassName="!max-h-none !overflow-visible"
              />
              <Input
                label={t('invoices.accountHolder')}
                {...register('accountHolder')}
                placeholder={t('invoices.accountHolderPlaceholder')}
                autoComplete="off"
              />
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Input
                  label={t('invoices.ibanLabel')}
                  {...register('iban', {
                    onChange: (e) => {
                      setValue('iban', formatIbanDisplay(e.target.value), {
                        shouldDirty: true,
                      });
                    },
                  })}
                  placeholder={t('invoices.ibanPlaceholder')}
                  autoComplete="off"
                />
                <Input
                  label={t('invoices.bicLabel')}
                  {...register('bic', {
                    onChange: (e) => {
                      setValue('bic', normalizeBic(e.target.value), {
                        shouldDirty: true,
                      });
                    },
                  })}
                  placeholder={t('invoices.bicPlaceholder')}
                  autoComplete="off"
                />
              </div>
            </div>
          </CollapsibleSection>

          <CollapsibleSection
            title={t('invoices.pdfNotes')}
            open={openSections.notes}
            onToggle={() => toggleSection('notes')}
            summary={notesSummary}
            complete={isSectionDone('notes')}
            icon={<StickyNote className="h-4 w-4" aria-hidden />}
          >
            <TextArea
              placeholder={t('invoices.notesPlaceholder')}
              rows={3}
              {...register('notes')}
            />
          </CollapsibleSection>

          {formError && (
            <p className="text-sm text-red-600" role="alert">
              {formError}
            </p>
          )}
        </div>

        <InvoicePreview
              sticky
              model={{
                invoiceNumber: formValues.invoiceNumber || '',
                invoiceName: formValues.invoiceName || '',
                issuedAtLabel: previewIssuedAtLabel,
                seller: liveSeller,
                buyer: liveBuyer,
                projectName: projectLabel,
                amountLabel,
                paymentDateLabel:
                  payment.status === 'paid' && payment.paymentDate
                    ? formatDateShort(payment.paymentDate)
                    : undefined,
                statusLabel,
                notes: formValues.notes,
                paymentTerms: composedPaymentTerms,
                paymentMethodLabel,
                accountHolder: formValues.accountHolder,
                iban: formValues.iban,
                bic: formValues.bic,
                lineItems: previewLines,
                labels: {
                  preview: t('invoices.preview'),
                  title: t('invoices.pdfTitle'),
                  from: t('invoices.pdfFrom'),
                  to: t('invoices.pdfTo'),
                  project: t('invoices.project'),
                  amount: t('invoices.pdfAmount'),
                  paymentDate: t('invoices.pdfPaymentDate'),
                  status: t('invoices.pdfStatus'),
                  notes: t('invoices.pdfNotes'),
                  paymentTerms: t('invoices.paymentTerms'),
                  paymentSection: t('invoices.paymentSection'),
                  paymentMethod: t('invoices.paymentMethod'),
                  accountHolder: t('invoices.accountHolder'),
                  ibanLabel: t('invoices.ibanLabel'),
                  bicLabel: t('invoices.bicLabel'),
                  siret: t('invoices.pdfSiret'),
                  vatNumber: t('invoices.pdfVatNumber'),
                  issuedAt: t('invoices.issuedAt'),
                  item: t('invoices.itemColumn'),
                  qty: t('invoices.qty'),
                  unitPrice: t('invoices.unitPrice'),
                  vatRate: t('invoices.vatRate'),
                  total: t('invoices.total'),
                },
              }}
            />
      </div>

      <div className="fixed inset-x-0 bottom-0 z-20 border-t border-gray-200 bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/90 md:left-20">
        <div className="flex items-center justify-end gap-2 px-3 py-3 sm:px-4 lg:px-6">
          <Button type="button" variant="ghost" size="md" onClick={onCancel} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" variant="primary" size="md" disabled={submitting || !ready} loading={submitting}>
            {t('invoices.create')}
          </Button>
        </div>
      </div>
    </form>
  );
};

export default IssueInvoiceForm;
